import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * Generic "re-render me on variant change" wrapper. Used by the PDP brew
 * table and ingredients accordion row; reusable by any variant-dependent fragment.
 *
 * Strictly reuses the section HTML the variant picker already fetched (zero extra requests):
 * on `variant:update` it finds its counterpart by `data-fragment-id` in the payload and morphs
 * itself, preserving any nested `details[open]` state so keyboard/SR context never jumps.
 *
 * @extends {Component<{}>}
 */
class VariantFragmentComponent extends Component {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @param {Event} event */
  #onVariantUpdate = event => {
    const customEvent = /** @type {import("@theme/events").VariantUpdateEvent} */ (event);
    if (customEvent.detail?.data?.productId !== this.dataset.productId) {
      return;
    }
    const source = customEvent.detail.data.html;
    if (!source) {
      return;
    }
    const fragmentId = this.dataset.fragmentId;
    if (!fragmentId) {
      return;
    }
    const replacement = source.querySelector(`variant-fragment-component[data-fragment-id="${fragmentId}"]`);
    if (!replacement) {
      return;
    }

    const openStates = [...this.querySelectorAll("details")].map(details => details.open);
    morph(this, replacement);
    for (const [index, details] of [...this.querySelectorAll("details")].entries()) {
      const wasOpen = openStates[index];
      if (typeof wasOpen === "boolean") {
        details.open = wasOpen;
      }
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const target = this.closest(".shopify-section, dialog");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }
}

if (!customElements.get("variant-fragment-component")) {
  customElements.define("variant-fragment-component", VariantFragmentComponent);
}

export { VariantFragmentComponent };
